import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { InfoBox } from '@motorway/mw-highway-code/alpha';

import { getEnquiryData } from 'PrivateApis';

import { DOCUMENT_KINDS, DOCUMENT_UPLOAD_TYPE } from '../DocumentCapture.helpers';
import { DocumentCaptureOfferContext, FinanceSettlementProviderContext } from '../DocumentCaptureContexts';
import ProofOfAddressMismatchInfoBox from '../ProofOfAddressMismatchInfoBox/ProofOfAddressMismatchInfoBox';

import { DocumentErrorsInfobox } from './documentErrorsInfobox/DocumentErrorsInfobox';
import {
  FinanceProviderCompleteSection,
} from './FinanceProviderSection/FinanceProviderCompleteSection';
import { FinanceProviderSection } from './FinanceProviderSection/FinanceProviderSection';
import { useImageManager } from './hooks/useImageManager';
import UploadDocumentsSection from './UploadDocumentsSection/UploadDocumentsSection';
import { getExitPath, getFinanceErrorMessage } from './UploadDocuments.helpers';
import type { UploadDocumentsPageProps } from './UploadDocumentsPage.types';
import { UploadDocumentsSummarySection } from './UploadDocumentsSummarySection';

import styles from './UploadDocumentsPageView.scss';

export const exitPath = ({ type, vrm }: { type: DOCUMENT_UPLOAD_TYPE; vrm: string }) => {
  const exitPathname = getExitPath(type);

  return `/${vrm}/${exitPathname}`;
};

export const UploadDocumentsPageView = ({
  document,
  kind,
  uploadDocType = DOCUMENT_UPLOAD_TYPE.DEFAULT,
}: UploadDocumentsPageProps) => {
  const { id: dpEnquiryId } = useContext(DocumentCaptureOfferContext) || {};
  const { pathname } = useLocation();
  const vrm = pathname.split('/')[1];

  const [sellingVrm, setSellingVrm] = useState<string | null>(null);

  useEffect(() => {
    if (dpEnquiryId) {
      getEnquiryData(dpEnquiryId, {}).then((data) => {
        setSellingVrm(data?.sellingVrm ?? null);
      });
    }
  }, [dpEnquiryId]);

  const { showErrorBox } = useImageManager();

  const isUtilityBill = kind === DOCUMENT_KINDS.UTILITY_BILL;

  const isAddressMismatch = uploadDocType === DOCUMENT_UPLOAD_TYPE.EARLY
    ? isUtilityBill : document?.hasAddressMismatch
      || false;
  const isPOAReupload = isUtilityBill
      && document?.images?.some((image) => image?.actions?.needsReupload?.value === true);

  const isPOAVisible = isAddressMismatch && !isPOAReupload;

  const docIsFinanceLetter = kind === DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER;
  const financePageEnabled = docIsFinanceLetter;

  const { financeAgreement } = useContext(FinanceSettlementProviderContext);

  const { automatedFinanceProvider, failedLookupReason, optedOut, settlementDetails } = financeAgreement || {};

  let financeSection = null;

  const showAutomatedFslFlow = (financePageEnabled && automatedFinanceProvider && !optedOut && !failedLookupReason);

  if (showAutomatedFslFlow) {
    if (settlementDetails) {
      financeSection = (
        <FinanceProviderCompleteSection
          enquiryId={String(dpEnquiryId)}
          provider={automatedFinanceProvider}
          settlementDetails={settlementDetails}
        />
      );
    } else {
      financeSection = (
        <FinanceProviderSection
          enquiryId={String(dpEnquiryId)}
          provider={automatedFinanceProvider}
          {...(sellingVrm ? { sellingVrm } : {})}
          vrm={vrm}
        />
      );
    }
  }

  return (
    <>
      {financeSection ?? (
        <>
          {isPOAVisible && <ProofOfAddressMismatchInfoBox kind={kind}/>}
          {showErrorBox && <DocumentErrorsInfobox/>}
          {failedLookupReason && (
            <div className={styles.infoBoxContainer} data-testid="finance-error-box">
              <InfoBox
                content={getFinanceErrorMessage(failedLookupReason).content}
                heading={getFinanceErrorMessage(failedLookupReason).heading}
                size="regular"
                variant="error"
              />
            </div>
          )}
          <UploadDocumentsSummarySection/>
          <UploadDocumentsSection exitPath={exitPath({ type: uploadDocType, vrm })}/>
        </>
      )}
    </>
  );
};
